import { DiscussionContextType, QUERY_KEYS } from "@cnx/common";
import { getQueryKeysForDiscussionContext } from "utils/chat";
import { Box, Divider, Flex, Popover, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconLogout } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import HeaderLogo from "components/HeaderLogo";
import NotificationWidget from "components/NotificationWidget";
import UserAvatar from "components/UserAvatar";
import useLogout from "hooks/useLogout";
import { useUnreadMessagesCount } from "hooks/useUnreadMessagesCount";
import IconMessageDotsCircle from "icons/common/message-dots-circle.svg";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useSocket } from "src/socket/useSocket";
import { useChatPanelStore } from "stores/chatPanel";
import { useAuthStore } from "stores/authStore";
import { HEADER_HEIGHT } from "../../common/constants";

interface DesktopHeaderProps {}

const DesktopHeader: FC<DesktopHeaderProps> = ({}) => {
  const { t } = useTranslation("common");
  const { logout, isPending } = useLogout();

  const currentUserId = useAuthStore(state => state.currentUserId);
  const avatar = useAuthStore(state => state.avatar);
  const email = useAuthStore(state => state.email);
  const fullName = useAuthStore(state => state.fullName);
  const socket = useSocket();
  const queryClient = useQueryClient();
  const { unreadMessagesCount, isChatEnabled } = useUnreadMessagesCount();
  const isPanelOpen = useChatPanelStore(store => store.isPanelOpen);
  const setIsPanelOpen = useChatPanelStore(store => store.setIsPanelOpen);
  const [, { close }] = useDisclosure(false);

  useEffect(() => {
    if (!socket || !currentUserId || !isChatEnabled) return;

    const invalidateQueries = ({
      context,
    }: { context?: DiscussionContextType } = {}) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DISCUSSIONS],
      });

      if (context) {
        getQueryKeysForDiscussionContext(context)?.forEach(queryKey => {
          queryClient.invalidateQueries({ queryKey });
        });
      }
    };

    const invalidateMessageQueries = ({
      discussionId,
      senderId,
    }: {
      discussionId: string;
      senderId?: string;
    }) => {
      if (senderId === currentUserId) return;
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MESSAGES, discussionId],
      });
    };

    socket.on("invalidateDiscussionQueries", invalidateQueries);
    socket.on("invalidateMessageQueries", invalidateMessageQueries);

    return () => {
      socket.off("invalidateDiscussionQueries", invalidateQueries);
      socket.off("invalidateMessageQueries", invalidateMessageQueries);
    };
  }, [socket, currentUserId, isChatEnabled, queryClient]);

  return (
    <Box
      px={"sm"}
      className="bg-white flex items-center shadow-sm sticky top-0 z-30 w-full"
      component={"header"}
      h={HEADER_HEIGHT}
    >
      <Flex w={"100%"} justify={"space-between"} align={"center"}>
        <Link to="/home" onClick={close}>
          <HeaderLogo />
        </Link>
        <Flex gap={"sm"}>
          {isChatEnabled && (
            <button
              type="button"
              aria-label="Open chat"
              className="m-2 relative cursor-pointer"
              onClick={() => setIsPanelOpen(!isPanelOpen)}
            >
              {unreadMessagesCount > 0 ? (
                <>
                  <Flex
                    justify="center"
                    align="center"
                    fz={10}
                    w={16}
                    h={16}
                    bg={"var(--color-blue)"}
                    className="text-white rounded-full absolute"
                    style={{
                      top: 0,
                      left: 0,
                      transform: "translate(-25%, -20%)",
                      zIndex: 2,
                      pointerEvents: "none",
                    }}
                  >
                    {unreadMessagesCount}
                  </Flex>
                  <IconMessageDotsCircle
                    width={24}
                    height={24}
                    color="var(--color-blue)"
                  />
                </>
              ) : (
                <IconMessageDotsCircle width={24} height={24} color="black" />
              )}
            </button>
          )}
          <NotificationWidget />
          <Popover
            radius={"15px"}
            keepMounted
            classNames={{ dropdown: "!p-0 min-w-[312px] !border-gray" }}
            position="bottom-end"
          >
            <Popover.Target>
              <button className="cursor-pointer">
                <UserAvatar userName={fullName} avatarId={avatar} />
              </button>
            </Popover.Target>
            <Popover.Dropdown>
              <Flex align={"center"} fz={"sm"} gap={"sm"} p={10}>
                <Flex
                  justify={"center"}
                  align={"center"}
                  className="text-white w-12 h-12 rounded-full"
                >
                  <UserAvatar userName={fullName} avatarId={avatar} />
                </Flex>
                <Box maw={300}>
                  {fullName && (
                    <Text fw={700} lineClamp={1} lh={1} c={"gray.8"}>
                      {fullName}
                    </Text>
                  )}
                  {email && (
                    <Text c={"gray.8"} lineClamp={1} lh={"sm"} fz={"sm"}>
                      {email}
                    </Text>
                  )}
                </Box>
              </Flex>
              <Divider color={"gray.1"} />

              <button
                data-testid="logout-button"
                disabled={isPending}
                onClick={logout}
                className="flex items-center p-2 gap-1 text-gray-light text-xs cursor-pointer"
              >
                <IconLogout className="text-gray-dark" />
                <span className="text-gray-dark font-bold">{t("logout")}</span>
              </button>
            </Popover.Dropdown>
          </Popover>
        </Flex>
      </Flex>
    </Box>
  );
};

export default DesktopHeader;
